import { Box, Step, StepContent, StepLabel, Stepper, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { DocCallout, DocCodeBlock, DocSection } from '../docs-components';

const QuickStart: FC = () => {
  const [activeStep] = useState(-1); // All expanded

  const steps = [
    {
      label: 'Create an API Key',
      content: (
        <>
          <Typography variant="body1" gutterBottom>
            Navigate to the <strong>API Keys</strong> tab and click <em>Create new API Key</em>. You will be asked to verify physical access to the
            device by entering a code shown on the device display.
          </Typography>
          <Typography variant="body1">
            After verification, choose a description, expiry date, and permission scope for the key, then click <em>Create Key</em>. Copy the key
            immediately — it will not be shown again.
          </Typography>
        </>
      )
    },
    {
      label: 'Make your first request',
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Fetch the current status of your device using the key you just created:
          </Typography>
          <DocCodeBlock language="bash">
            {`curl https://api.thermiaonline.com/v1/devices/YOUR_DEVICE_ID/status \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
          </DocCodeBlock>
          <Typography variant="body1" sx={{ mt: 1.5 }}>
            A successful response looks like this:
          </Typography>
          <DocCodeBlock language="json">
            {`{
  "deviceId": "abc123",
  "indoorTemperature": 21.5,
  "outdoorTemperature": -3.2,
  "hotWaterTemperature": 48.0,
  "operatingMode": "heating",
  "isOnline": true,
  "lastSeen": "2026-03-04T10:22:00Z"
}`}
          </DocCodeBlock>
        </>
      )
    },
    {
      label: 'Parse and use the data',
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Here is a simple Node.js example that fetches device status and logs the indoor temperature:
          </Typography>
          <DocCodeBlock language="javascript">
            {`const API_KEY = process.env.THERMIA_API_KEY;
const DEVICE_ID = process.env.THERMIA_DEVICE_ID;

const res = await fetch(
  \`https://api.thermiaonline.com/v1/devices/\${DEVICE_ID}/status\`,
  { headers: { Authorization: \`Bearer \${API_KEY}\` } }
);

if (!res.ok) throw new Error(\`API error: \${res.status}\`);

const { indoorTemperature, operatingMode } = await res.json();
console.log(\`Indoor: \${indoorTemperature}°C — Mode: \${operatingMode}\`);`}
          </DocCodeBlock>
        </>
      )
    },
    {
      label: '(Optional) Subscribe to events',
      content: (
        <>
          <Typography variant="body1" gutterBottom>
            Instead of polling for status changes, register a webhook endpoint to receive push notifications whenever your device state changes. See
            the <strong>Webhooks</strong> guide for full details.
          </Typography>
          <DocCallout type="info">
            Webhooks require a publicly reachable HTTPS endpoint. During development you can use a tunnelling service like{' '}
            <a href="https://ngrok.com" target="_blank" rel="noreferrer">
              ngrok
            </a>
            .
          </DocCallout>
        </>
      )
    }
  ];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Quick Start Guide
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Get up and running with the Thermia API in under 10 minutes.
      </Typography>

      <Stepper orientation="vertical" nonLinear activeStep={activeStep}>
        {steps.map((step, index) => (
          <Step key={step.label} active expanded>
            <StepLabel StepIconProps={{ sx: { fontSize: '1.4rem' } }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Step {index + 1}: {step.label}
              </Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ mt: 1, mb: 2 }}>{step.content}</Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      <DocSection title="Next Steps">
        <Box component="ul" sx={{ pl: 3, '& li': { mb: 0.75 } }}>
          <li>
            <Typography variant="body1">
              Explore all available endpoints in the <strong>Explore the API</strong> tab.
            </Typography>
          </li>
          <li>
            <Typography variant="body1">Learn about rate limits to avoid throttling.</Typography>
          </li>
          <li>
            <Typography variant="body1">Set up webhooks for real-time event notifications.</Typography>
          </li>
          <li>
            <Typography variant="body1">Review our error handling guide for robust integrations.</Typography>
          </li>
        </Box>
      </DocSection>
    </Box>
  );
};

export default QuickStart;
